import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { type BoundaryTerritoryGroupsAction } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import {
  BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS, BOUNDARY_TERRITORY_GROUP_REMOVE_SUCCESS,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import { activeBTGIdPerBoundaryGroupChanged } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import type { MapSettingsBoundariesAction } from '../../boundaries/mapSettingsBoundaries.action';
import { MAP_SETTINGS_BOUNDARIES_SET_PRIMARY_BOUNDARY_GROUP_ID } from '../../boundaries/mapSettingsBoundaries.actionTypes';
import { type MapSettingsToolsStateBoundaryStateAction } from './mapSettingsToolsStateBoundary.action';
import {
  MAP_SETTINGS_TOOLS_STATE_BOUNDARY_CLEAR_LOCATIONS_FILTER,
  MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_FILTER, MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_LOCATIONS_FILTER,
} from './mapSettingsToolsStateBoundary.actionTypes';
import {
  isBoundaryGroupHidden, type MapSettingsToolsStateBoundaryState,
} from './mapSettingsToolsStateBoundary.state';

export const mapSettingsToolsStateBoundaryInitialState: MapSettingsToolsStateBoundaryState = {
  filters: {},
  locationsFilter: {},
  activeBTGIdPerBoundaryGroup: {},
};

type Action =
  MapSettingsToolsStateBoundaryStateAction
  | BoundaryTerritoryGroupsAction
  | MapSettingsBoundariesAction
  | ReturnType<typeof activeBTGIdPerBoundaryGroupChanged>;

export const mapSettingsToolsStateBoundaryReducer = (state = mapSettingsToolsStateBoundaryInitialState, action: Action): MapSettingsToolsStateBoundaryState => {
  if (activeBTGIdPerBoundaryGroupChanged.match(action)) {
    const { boundaryGroupId, activeBTGId } = action.payload;

    return copyAndDeleteFilter({
      ...state,
      activeBTGIdPerBoundaryGroup: {
        ...state.activeBTGIdPerBoundaryGroup,
        [boundaryGroupId]: activeBTGId,
      },
    }, boundaryGroupId);
  }

  switch (action.type) {
    case MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.payload.boundaryGroupId]: action.payload.filter,
        },
      };

    case MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_LOCATIONS_FILTER:
      return {
        ...state,
        locationsFilter: {
          [action.payload.boundaryGroupId]: action.payload.filter,
        },
      };
    case MAP_SETTINGS_TOOLS_STATE_BOUNDARY_CLEAR_LOCATIONS_FILTER:
      return {
        ...state,
        locationsFilter: {},
      };

    case BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS:
      return action.payload.boundaryTerritoryGroups.reduce(
        (updatedState, { boundaryGroupId }) => copyAndDeleteFilter(updatedState, boundaryGroupId),
        state,
      );

    case BOUNDARY_TERRITORY_GROUP_REMOVE_SUCCESS:
      return action.payload.boundaryTerritoryGroups.reduce((updatedState, { boundaryGroupId }) => {
        return copyAndDeleteFilter(updatedState, boundaryGroupId);
      }, state);

    case MAP_SETTINGS_BOUNDARIES_SET_PRIMARY_BOUNDARY_GROUP_ID:
      if (action.payload.primaryBoundaryGroupId) {
        const filter = state.filters[action.payload.primaryBoundaryGroupId];
        if (filter && isBoundaryGroupHidden(filter)) {
          return copyAndDeleteFilter(state, action.payload.primaryBoundaryGroupId);
        }
      }

      return state;

    default:
      return state;
  }
};

const copyAndDeleteFilter = (state: MapSettingsToolsStateBoundaryState, boundaryGroupId: BoundaryGroupId) => {
  const filters = { ...state.filters };
  delete filters[boundaryGroupId];
  const locationsFilter = { ...state.locationsFilter };
  delete locationsFilter[boundaryGroupId];

  return {
    ...state,
    filters,
    locationsFilter,
  };
};
